// Catalog.ID Client — Claims API
// ============================================================================

import { api, ApiError } from './client';
import type {
    CreateClaimRequest, CreateClaimResponse,
    ClaimSummary, ClaimDetail,
    UpdateClaimRequest,
    ShareClaimRequest,
    RevokeClaimRequest,
    RevokeShareRequest,
    ValidateClaimRequest, ValidateClaimResponse,
    RegistryKeyVerifyResponse,
    SharedClaimSummary,
} from './types';

export async function createClaim(req: CreateClaimRequest): Promise<CreateClaimResponse> {
    return api.post('/api/claims', req);
}

export async function listClaims(): Promise<ClaimSummary[]> {
    return api.get('/api/claims');
}

export async function getClaim(claimUuid: string): Promise<ClaimDetail> {
    return api.get(`/api/claims/${claimUuid}`);
}

export async function updateClaim(claimUuid: string, req: UpdateClaimRequest): Promise<void> {
    return api.put(`/api/claims/${claimUuid}`, req);
}

export async function deleteClaim(claimUuid: string): Promise<void> {
    return api.del(`/api/claims/${claimUuid}`);
}

export async function shareClaim(claimUuid: string, req: ShareClaimRequest): Promise<void> {
    return api.post(`/api/claims/${claimUuid}/share`, req);
}

export async function revokeClaim(claimUuid: string, req: RevokeClaimRequest): Promise<void> {
    return api.post(`/api/claims/${claimUuid}/revoke`, req);
}

/**
 * Withdraw ONE recipient's access to a claim (chat migration plan §4).
 * Distinct from `revokeClaim`, which revokes the claim as a whole and does not
 * cut recipient read access at all.
 *
 * 404 — no such claim, or no share for that recipient.
 * 400 — the server refused the recipient (the owner's own CID, or an operator
 *       review key); its message says which, and is meant to be shown as-is.
 */
export async function revokeShare(claimUuid: string, req: RevokeShareRequest): Promise<void> {
    return api.post(`/api/claims/${claimUuid}/revoke-share`, req);
}

export async function validateClaim(claimUuid: string, req: ValidateClaimRequest): Promise<ValidateClaimResponse> {
    return api.post(`/api/claims/${claimUuid}/validate`, req);
}

/**
 * Actionable copy for a validation failure the server classified, when it
 * relayed a non-PII reason category (validator `VerifyStartError` /
 * `ConstraintError` → core 502 `reason`). Returns null when there is no
 * recognised reason, so callers fall back to their own generic message. The
 * category never carries PII (no address, no phone number, no provider
 * free-text).
 *
 * The phone-specific reasons come from the SMS provider; `value_mismatch` and
 * `invalid_constraint` come from a FIXED-VALUE invitation (§14.2) and can land
 * on either channel, which is why the map is shared and both steps consult it.
 * The value_mismatch wording is the one §14.2 pins for the member: the
 * invitation is for a specific address, and the card says which.
 */
/** Shown when a fixed-value invitation refuses what the member typed. Exported
 *  so the wizard's own copy and this failure message stay one sentence. */
export const FIXED_VALUE_MISMATCH_MESSAGE =
    'Your invitation is for a specific address — enter the one printed on your card.';

const VALIDATION_REASON_MESSAGES: Record<string, string> = {
    invalid_number:
        'That phone number doesn’t look valid. Check the number and include the '
        + 'country code (for example, +31 6 12345678).',
    unsupported_region:
        'We can’t send an SMS to that number’s country. Please use a mobile '
        + 'number in a supported country.',
    provider_unavailable:
        'The SMS service is temporarily unavailable. Please wait a moment and try again.',
    value_mismatch: FIXED_VALUE_MISMATCH_MESSAGE,
    invalid_constraint:
        'We could not check this against your invitation. Reopen the link or the code '
        + 'from your invitation card and try again.',
};

function validationReason(err: unknown): string | null {
    if (err instanceof ApiError && err.body !== null && typeof err.body === 'object'
        && 'reason' in err.body) {
        const reason = (err.body as { reason?: unknown }).reason;
        if (typeof reason === 'string') return reason;
    }
    return null;
}

/** Actionable copy for any classified validation failure, or null. */
export function claimValidationErrorMessage(err: unknown): string | null {
    const reason = validationReason(err);
    return reason !== null && reason in VALIDATION_REASON_MESSAGES
        ? VALIDATION_REASON_MESSAGES[reason] : null;
}

/** {@link claimValidationErrorMessage} under its original name, kept because the
 *  phone step and its tests have used it since M13. */
export const phoneValidationErrorMessage = claimValidationErrorMessage;

/** Get the validator's hybrid KEM public key for claim key re-encryption. */
export async function getValidatorKey(): Promise<{ publicKey: string }> {
    return api.get('/api/claims/validator-key');
}

/** Submit Registry key ownership challenge-response: sign the challenge and verify. */
export async function verifyRegistryKey(challenge: string, signature: string, publicKey: string): Promise<RegistryKeyVerifyResponse> {
    return api.post(`/api/claims/verify-registry-key/${encodeURIComponent(challenge)}`, { signature, publicKey });
}

/** Verify domain ownership: tell the validator to fetch .well-known/catalogid.txt. */
export async function verifyDomain(token: string, domain: string): Promise<{ success: boolean; certificate?: string }> {
    return api.post(`/api/verify/${encodeURIComponent(token)}/domain`, { domain });
}

/** Verify a phone number: submit the SMS one-time code for the validator to check. */
/** `forCid`: a logged-out flow for that identity (the reset ceremony), whose
 *  job lives at the identity's origin — sent there, without a session token. */
export async function verifyPhone(
    token: string, code: string, forCid?: string,
): Promise<{ success: boolean; certificate?: string }> {
    return api.post(`/api/verify/${encodeURIComponent(token)}/code`, { code },
        forCid ? { cid: forCid, anonymous: true } : undefined);
}

/** Cancel an active validation job for a claim. */
export async function cancelValidation(claimUuid: string): Promise<void> {
    return api.del(`/api/claims/${claimUuid}/validate`);
}

export async function listSharedClaims(): Promise<SharedClaimSummary[]> {
    return api.get('/api/claims/shared');
}
